import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  Badge,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faFolder,
  faFolderOpen,
  faCloudArrowDown,
  faArrowRight,
  faSearch,
  faHome,
  faFolderPlus,
  faXmark,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../firebaseContext";
import { addLog, storage } from "../../firebase";

export default function Folder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, admin } = useAuth();

  const [folderPath, setFolderPath] = useState(
    () => location.state?.folder ?? null
  );
  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (location.state?.folder && location.state.folder !== folderPath) {
      setFolderPath(location.state.folder);
    }
  }, [location.state?.folder, folderPath]);

  useEffect(() => {
    if (!folderPath) return;

    let isMounted = true;

    const fetchFolderContents = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
          setSubfolders([]);
          setFiles([]);
        }

        const folderRef = ref(storage, folderPath);
        const res = await listAll(folderRef);

        if (!isMounted) return;

        setSubfolders(
          res.prefixes.map((childRef) => [childRef.name, childRef])
        );

        const fetchedFiles = await Promise.all(
          res.items.map(async (itemRef) => [
            itemRef.name,
            await getDownloadURL(itemRef),
            itemRef.parent,
          ])
        );

        if (!isMounted) return;

        setFiles(fetchedFiles);
      } catch (err) {
        if (isMounted) {
          console.error("Unable to load folder contents", err);
          setError(
            "We couldn't load this folder right now. Please refresh or try again shortly."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchFolderContents();

    return () => {
      isMounted = false;
    };
  }, [folderPath]);

  useEffect(() => {
    setSearchTerm("");
  }, [folderPath]);

  const sortFunction = useCallback(
    (a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()),
    []
  );

  const handleDownloadButton = (filename, fileURL) => {
    if (currentUser?.email) {
      addLog(currentUser.email, filename, "Downloaded");
    }
    const anchor = document.createElement("a");
    anchor.href = fileURL;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleSelectFolder = useCallback(
    (folderRef) => {
      navigate(`/folder/${encodeURIComponent(folderRef.name)}`, {
        state: { folder: folderRef.fullPath },
      });
    },
    [navigate]
  );

  const handleAdminButton = useCallback(() => {
    navigate("/addFolder", {
      state: { path: folderPath, type: "files" },
    });
  }, [navigate, folderPath]);

  const handleBackToDownloads = useCallback(() => {
    navigate("/downloads");
  }, [navigate]);

  const folderSegments = useMemo(
    () => (folderPath ? folderPath.split("/") : []),
    [folderPath]
  );

  const relativeSegments = useMemo(() => {
    if (folderSegments.length > 1) {
      return folderSegments.slice(1);
    }
    return folderSegments;
  }, [folderSegments]);

  const folderName = useMemo(() => {
    if (!folderSegments.length) return "Folder";
    return folderSegments[folderSegments.length - 1] || "Folder";
  }, [folderSegments]);

  const displayPath = useMemo(() => {
    const readable = relativeSegments.join(" / ");
    return readable || folderName;
  }, [relativeSegments, folderName]);

  const parentSegments = useMemo(
    () => folderSegments.slice(0, -1),
    [folderSegments]
  );

  const parentFolderPath = parentSegments.length
    ? parentSegments.join("/")
    : null;

  const parentFolderName =
    parentSegments.length > 1
      ? parentSegments[parentSegments.length - 1]
      : null;

  const sortedSubfolders = useMemo(
    () => [...subfolders].sort(sortFunction),
    [subfolders, sortFunction]
  );

  const sortedFiles = useMemo(
    () => [...files].sort(sortFunction),
    [files, sortFunction]
  );

  const filteredFiles = useMemo(() => {
    if (!searchTerm) {
      return sortedFiles;
    }
    const query = searchTerm.toLowerCase();
    return sortedFiles.filter((file) => file[0].toLowerCase().includes(query));
  }, [sortedFiles, searchTerm]);

  const getFileExtension = (filename) => {
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
  };

  const enhancedFiles = useMemo(
    () =>
      filteredFiles.map(([name, url]) => ({
        name,
        url,
        extension: getFileExtension(name),
        location: displayPath,
      })),
    [filteredFiles, displayPath]
  );

  const breadcrumbs = useMemo(() => {
    if (!folderPath) return [];

    const segments = folderSegments.filter(Boolean);
    const crumbList = [
      {
        label: "Downloads",
        type: "home",
        isCurrent: segments.length === 0,
      },
    ];

    let accumulated = "";
    segments.forEach((segment, index) => {
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      crumbList.push({
        label: segment,
        path: accumulated,
        segment,
        type: "folder",
        isCurrent: index === segments.length - 1,
      });
    });

    return crumbList;
  }, [folderPath, folderSegments]);

  const handleNavigateUp = useCallback(() => {
    if (parentFolderName && parentFolderPath) {
      navigate(`/folder/${encodeURIComponent(parentFolderName)}`, {
        state: { folder: parentFolderPath },
      });
      return;
    }

    if (parentSegments.length) {
      handleBackToDownloads();
    }
  }, [
    handleBackToDownloads,
    navigate,
    parentFolderName,
    parentFolderPath,
    parentSegments.length,
  ]);

  const handleBreadcrumbClick = useCallback(
    (crumb) => {
      if (crumb.isCurrent) return;
      if (crumb.type === "home") {
        handleBackToDownloads();
        return;
      }

      if (crumb.path && crumb.segment) {
        navigate(`/folder/${encodeURIComponent(crumb.segment)}`, {
          state: { folder: crumb.path },
        });
      }
    },
    [handleBackToDownloads, navigate]
  );

  if (!folderPath) {
    return (
      <Container fluid="lg" className="p-2">
        <Alert variant="danger">Code 404. Page not found!</Alert>
        <Alert variant="info">
          Note: Copying the URL from address bar doesn't work for file system
        </Alert>
        <Button onClick={handleBackToDownloads} className="w-100">
          Head to Downloads page
        </Button>
      </Container>
    );
  }

  const totalSubfolders = subfolders.length;
  const totalFiles = files.length;

  return (
    <div className="downloads-page folder-page">
      <section className="downloads-hero-wrapper py-5">
        <Container fluid="lg">
          <div className="downloads-hero folder-hero">
            <Row className="align-items-center gy-4">
              <Col lg={7} className="downloads-hero-content">
                <div className="downloads-hero-badge">Current folder</div>
                <nav
                  aria-label="Folder breadcrumbs"
                  className="folder-breadcrumbs"
                >
                  <ol className="folder-breadcrumbs-list">
                    {breadcrumbs.map((crumb, index) => (
                      <li
                        key={`${crumb.label}-${index}`}
                        className={`folder-breadcrumb-item${
                          crumb.isCurrent ? " active" : ""
                        }`}
                      >
                        {crumb.isCurrent ? (
                          <span>{crumb.label}</span>
                        ) : (
                          <button
                            type="button"
                            className="folder-breadcrumb-link"
                            onClick={() => handleBreadcrumbClick(crumb)}
                          >
                            {crumb.label}
                          </button>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
                <h1 className="downloads-hero-title">{folderName}</h1>
                <p className="downloads-hero-subtitle folder-hero-path">
                  {displayPath}
                </p>
                <p className="downloads-hero-subtitle">
                  Browse subfolders, search documents, or jump back to the
                  downloads library without losing your place.
                </p>
                <div className="downloads-hero-actions">
                  <Button
                    variant="light"
                    className="downloads-hero-action"
                    onClick={handleBackToDownloads}
                  >
                    <FontAwesomeIcon icon={faHome} className="me-2" />
                    Back to downloads
                  </Button>
                  {parentSegments.length ? (
                    <Button
                      variant="outline-light"
                      className="downloads-hero-action"
                      onClick={handleNavigateUp}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                      Go up one level
                    </Button>
                  ) : null}
                  {admin && (
                    <Button
                      variant="light"
                      className="downloads-hero-action"
                      onClick={handleAdminButton}
                    >
                      <FontAwesomeIcon icon={faFolderPlus} className="me-2" />
                      Add folder & files
                    </Button>
                  )}
                </div>
              </Col>
              <Col lg={5}>
                <Card className="downloads-hero-card h-100">
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={12} sm={6}>
                        <div className="downloads-stat-card">
                          <div className="downloads-stat-icon downloads-stat-icon-folders">
                            <FontAwesomeIcon icon={faFolderOpen} />
                          </div>
                          <div>
                            <div className="downloads-stat-value">
                              {totalSubfolders}
                            </div>
                            <div className="downloads-stat-label">
                              Subfolders
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="downloads-stat-card">
                          <div className="downloads-stat-icon downloads-stat-icon-files">
                            <FontAwesomeIcon icon={faFile} />
                          </div>
                          <div>
                            <div className="downloads-stat-value">
                              {totalFiles}
                            </div>
                            <div className="downloads-stat-label">
                              Files here
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="downloads-tip-card">
                          <h3 className="downloads-tip-title">
                            Need a different folder?
                          </h3>
                          <p className="downloads-tip-copy">
                            Use the subfolder cards or jump back to the
                            downloads hub to open another location in seconds.
                          </p>
                          <Button
                            variant="outline-primary"
                            className="downloads-tip-btn"
                            onClick={handleBackToDownloads}
                          >
                            Return to downloads
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="ms-2"
                            />
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      <Container fluid="lg" className="downloads-content pb-5">
        {error && (
          <Alert variant="danger" className="downloads-alert">
            {error}
          </Alert>
        )}

        {(loading || totalSubfolders > 0) && (
          <section className="downloads-section mb-4">
            <div className="downloads-section-header">
              <div>
                <h2 className="downloads-section-title">Subfolders</h2>
                <p className="downloads-section-subtitle">
                  Drill down into the next level. Open any folder below to view
                  its dedicated files and resources.
                </p>
              </div>
            </div>

            {loading ? (
              <div className="downloads-loading-panel">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <Row className="g-4">
                {sortedSubfolders.map(([name, refItem]) => (
                  <Col key={refItem.fullPath} xs={12} md={6} xl={4}>
                    <Card
                      className="downloads-folder-card h-100"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectFolder(refItem)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleSelectFolder(refItem);
                        }
                      }}
                    >
                      <Card.Body className="downloads-folder-body">
                        <div className="downloads-folder-icon">
                          <FontAwesomeIcon icon={faFolder} />
                        </div>
                        <h3
                          className="downloads-folder-name text-truncate mb-0"
                          title={name}
                        >
                          {name}
                        </h3>
                        <div className="downloads-folder-arrow">
                          <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </section>
        )}

        {(loading || totalFiles > 0) && (
          <section className="downloads-section">
            <div className="downloads-section-header flex-column flex-lg-row align-items-lg-center">
              <div>
                <h2 className="downloads-section-title">
                  Files in this folder
                </h2>
                <p className="downloads-section-subtitle">
                  Search by name or scan the grid to quickly download what you
                  need.
                </p>
              </div>
              <Form
                className="downloads-search-form mt-3 mt-lg-0"
                onSubmit={(event) => event.preventDefault()}
              >
                <div className="downloads-search-input">
                  <FontAwesomeIcon icon={faSearch} />
                  <Form.Control
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search files in this folder..."
                  />
                  {searchTerm && (
                    <Button
                      variant="link"
                      className="downloads-search-clear"
                      onClick={() => setSearchTerm("")}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                      <span className="visually-hidden">Clear search</span>
                    </Button>
                  )}
                </div>
              </Form>
            </div>

            {loading ? (
              <div className="downloads-loading-panel">
                <Spinner animation="border" role="status" />
              </div>
            ) : enhancedFiles.length > 0 ? (
              <Row className="g-4">
                {enhancedFiles.map((file) => (
                  <Col key={file.url} xs={12} md={6} xl={4}>
                    <Card className="downloads-file-card h-100">
                      <Card.Body>
                        <div className="downloads-file-head">
                          <div className="downloads-file-icon">
                            <FontAwesomeIcon icon={faFile} />
                          </div>
                          <Badge className="downloads-file-badge">
                            {file.extension}
                          </Badge>
                        </div>
                        <h3 className="downloads-file-name" title={file.name}>
                          {file.name}
                        </h3>
                        <p
                          className="downloads-file-location"
                          title={file.location}
                        >
                          {file.location}
                        </p>
                      </Card.Body>
                      <Card.Footer>
                        <Button
                          variant="primary"
                          className="downloads-file-btn"
                          onClick={() =>
                            handleDownloadButton(file.name, file.url)
                          }
                          title={`Download ${file.name}`}
                        >
                          <FontAwesomeIcon
                            icon={faCloudArrowDown}
                            className="me-2"
                          />
                          Download
                        </Button>
                      </Card.Footer>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Alert variant="light" className="downloads-empty">
                {searchTerm
                  ? "No files match your search right now. Try another keyword or clear the filter."
                  : "This folder doesn't contain any downloadable files yet."}
              </Alert>
            )}
          </section>
        )}
      </Container>
    </div>
  );
}
